import React, { useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import { Sprite } from '../Sprites/Sprite';
import type { SpriteName } from '../Sprites/Sprite';
import './HealthExpenditure.scss';

/**
 * 「人均醫療衞生開支」, an in-house replacement for the Piktochart of the same name, in the look of
 * ai.portfoplus.com: one bar a year since 1989/90, a tap on a bar to see that year against the
 * first, and a face that grows more worried as the cost climbs. The figures are the Health Bureau's
 * current health expenditure per capita, 1989/90 – 2024/25, excluding identified COVID-19 spending
 * (Table 1 of its Domestic Health Accounts). The Piktochart used the older "total" series, which ended at 2023/24 ($33,334).
 */

/** [year, HK$ per capita at current prices]. */
export const SERIES: [string, number][] = [
  ['1989/90', 3219],
  ['1990/91', 3875],
  ['1991/92', 4830],
  ['1992/93', 5640],
  ['1993/94', 6343],
  ['1994/95', 7127],
  ['1995/96', 7971],
  ['1996/97', 8644],
  ['1997/98', 9454],
  ['1998/99', 9813],
  ['1999/00', 9778],
  ['2000/01', 9915],
  ['2001/02', 10059],
  ['2002/03', 9913],
  ['2003/04', 10109],
  ['2004/05', 9892],
  ['2005/06', 10283],
  ['2006/07', 10478],
  ['2007/08', 11152],
  ['2008/09', 11886],
  ['2009/10', 12446],
  ['2010/11', 14308],
  ['2011/12', 15524],
  ['2012/13', 16833],
  ['2013/14', 18319],
  ['2014/15', 19624],
  ['2015/16', 21076],
  ['2016/17', 22178],
  ['2017/18', 23434],
  ['2018/19', 25003],
  ['2019/20', 26305],
  ['2020/21', 27227],
  ['2021/22', 29305],
  ['2022/23', 31480],
  ['2023/24', 33177],
  ['2024/25', 34890]
];

const LAST = SERIES.length - 1;
const MAX = 35000;
const value = (i: number) => SERIES[i][1];

/** Rise from the first year, per cent. */
export const risePct = (i = LAST) => Math.round((value(i) / value(0) - 1) * 100);
/** Times the first year, to one decimal. */
export const times = (i = LAST) => Math.round((value(i) / value(0)) * 10) / 10;
/** The face for a year: calm at the start, in despair once it is many times dearer. */
export const faceFor = (i: number): SpriteName => {
  const x = times(i);
  return x < 2.5 ? 'face-calm' : x < 5 ? 'face-hesitate' : x < 8 ? 'face-panic' : 'face-despair';
};

/** Average yearly growth from the first year, per cent to one decimal. */
export const yearlyPct = (i = LAST) => (i === 0 ? 0 : Math.round(((value(i) / value(0)) ** (1 / i) - 1) * 1000) / 10);

const SOURCE: Record<string, string> = {
  en: 'https://www.healthbureau.gov.hk/statistics/en/dha.htm',
  zh: 'https://www.healthbureau.gov.hk/statistics/chs/dha.htm'
};
const SOURCE_TC = 'https://www.healthbureau.gov.hk/statistics/cn/dha.htm';

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const money = (n: number) => `$${n.toLocaleString('en-US')}`;

const HealthExpenditure = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [picked, setPicked] = useState<number>(LAST);

  return (
    <div className="health-expenditure" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="he-head">
        <h2 className="he-title">
          <span>{t('he-title')}</span>
        </h2>
        <p className="he-sub">{t('he-sub', { from: SERIES[0][0], to: SERIES[LAST][0] })}</p>
      </header>

      <div className="he-readout" aria-live="polite">
        <Sprite name={faceFor(picked)} size={64} className="he-face" />
        <span>{SERIES[picked][0]}</span>
        <strong>{money(value(picked))}</strong>
        <em>{picked === 0 ? t('he-start') : t('he-times', { x: times(picked), from: SERIES[0][0] })}</em>
      </div>

      <div className="he-chart">
        <Sprite name="face-panic" size={70} className="he-scream" bob />
        {SERIES.map(([year], i) => (
          <button
            key={year}
            type="button"
            className={`he-bar${i === picked ? ' is-on' : ''}${i === LAST ? ' is-last' : ''}`}
            aria-label={`${year} ${money(value(i))}`}
            aria-pressed={i === picked}
            onClick={() => setPicked(i)}
            onMouseEnter={() => setPicked(i)}
          >
            <span style={{ height: `${(value(i) / MAX) * 100}%` }} />
          </button>
        ))}
      </div>
      <div className="he-axis" aria-hidden="true">
        <span>{SERIES[0][0]}</span>
        <span>{SERIES[LAST][0]}</span>
      </div>

      <div className="he-stats">
        <div className="he-stat is-rise">
          <span>{t('he-rise', { from: SERIES[0][0] })}</span>
          <strong>{risePct()}% ▲</strong>
        </div>
        <div className="he-stat is-yearly">
          <span>{t('he-yearly')}</span>
          <strong>{yearlyPct().toFixed(1)}% ▲</strong>
        </div>
      </div>
      <p className="he-note">{t('he-note')}</p>

      <div className="he-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('he-conclusion')}</p>
        <Sprite name="piggy-coin" size={88} className="he-piggy" bob />
      </div>

      <p className="he-source">
        {t('he-source')}
        <a href={SOURCE[intl.locale] || SOURCE_TC} target="_blank" rel="noopener noreferrer">
          {t('he-source-link')}
        </a>
        {t('he-source-note')}
      </p>
    </div>
  );
};

export default HealthExpenditure;
